import AccountCircle from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import DarkMode from '@mui/icons-material/DarkMode';
import FeedbackOutlined from '@mui/icons-material/FeedbackOutlined';
import Logout from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Settings from '@mui/icons-material/Settings';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import { useTheme } from '@mui/material/styles';
import { googleLogout } from '@react-oauth/google';
import React from 'react';

import Text from '../../base/text';

import { getDesignTokens } from '~/client/components/base/core';
import { useFeedbackMode } from '~/client/contexts/feedback-mode-context';
import { usePermissions } from '~/client/contexts/permissions-context';
import { useThemeMode } from '~/client/contexts/theme-context';
import { useUserProfile } from '~/client/hooks/user-profile';
import { orgAdminDetailsUtils, settingsRoutesUtil } from '~/client/routes';
import { nodeAuthApi } from '~/client/services';
import * as GQL from '~/graphql/generated/client';

interface Props {
  iconColor?: string;
}

export const TopBarAccountMenu: React.FC<Props> = ({
  iconColor = 'inherit',
}) => {
  const { navigateTo: settingNavigateTo } = settingsRoutesUtil.useHelper();
  const { navigateTo: orgAdminDetailsNavigateTo } =
    orgAdminDetailsUtils.useHelper();
  const [signOut] = nodeAuthApi.useLogoutMutation();
  const { effectiveMode, toggleMode } = useThemeMode();
  const feedbackMode = useFeedbackMode();
  const theme = useTheme();
  const tokens = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light'
  );
  const { userProfile, isLoading } = useUserProfile();
  const { hasCapability } = usePermissions();

  const { data } = GQL.useUserOrganizationContextsQuery({
    skip: !userProfile.emailVerified,
    fetchPolicy: 'cache-and-network',
  });

  // Keep the context query to resolve the correct organizationId for the nav link;
  // visibility is now controlled by capability, not raw role string.
  const orgAdminMembership = data?.userOrganizationContexts?.find(
    (membership: any) =>
      membership.status === 'ACTIVE' &&
      membership.organization?._id === userProfile.defaultOrganizationId
  );

  const canAccessOrgAdmin = hasCapability('org:manage_members');
  const canAccessSystemAdmin = hasCapability('platform:manage_system_admins');

  const currentOrgName = data?.userOrganizationContexts?.find(
    (ctx: any) => ctx?.organization?._id === userProfile.defaultOrganizationId
  )?.organization?.name;

  const initials =
    !isLoading && (userProfile.firstName || userProfile.lastName)
      ? `${(userProfile.firstName ?? '')[0] ?? ''}${(userProfile.lastName ?? '')[0] ?? ''}`.toUpperCase()
      : null;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    signOut();
    googleLogout();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('.MuiSwitch-root')) {
            return;
          }
          handleClose();
        }}
        PaperProps={{
          elevation: 0,
          sx: (t) => ({
            overflow: 'visible',
            boxShadow: t.shadows[2],
            mt: 1.5,
            minWidth: 240,
          }),
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        {/* Profile card */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {initials ? (
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: tokens.avatarBg,
                color: tokens.avatarText,
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>
          ) : (
            <AccountCircle
              style={{ fontSize: 36, color: iconColor, flexShrink: 0 }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Text sz="body14" noWrap sx={{ fontWeight: 600 }}>
              {userProfile.firstName} {userProfile.lastName}
            </Text>
            <Text sz="body12" noWrap sx={{ color: 'text.secondary' }}>
              {userProfile.email}
            </Text>
            {currentOrgName && (
              <Text sz="body12" noWrap sx={{ color: 'text.disabled' }}>
                {currentOrgName}
              </Text>
            )}
          </Box>
        </Box>

        <Divider />

        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleMode();
          }}
          disableRipple
        >
          <ListItemIcon>
            <DarkMode fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dark mode</ListItemText>
          <Switch
            size="small"
            checked={effectiveMode === 'dark'}
            readOnly
            color="primary"
            tabIndex={-1}
            sx={{ pointerEvents: 'none', ml: 1 }}
          />
        </MenuItem>

        <MenuItem onClick={feedbackMode.toggle} data-feedback-ui="toggle">
          <ListItemIcon>
            <FeedbackOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Feedback mode</ListItemText>
          <Switch
            size="small"
            checked={feedbackMode.active}
            readOnly
            color="primary"
            tabIndex={-1}
            sx={{ pointerEvents: 'none', ml: 1 }}
          />
        </MenuItem>

        <MenuItem onClick={() => settingNavigateTo('PERSONAL')}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Personal Settings
        </MenuItem>

        {(canAccessOrgAdmin || canAccessSystemAdmin) && <Divider />}

        {canAccessOrgAdmin && orgAdminMembership && (
          <MenuItem
            onClick={() =>
              orgAdminDetailsNavigateTo('DASHBOARD', {
                params: {
                  organizationId: orgAdminMembership.organization?._id ?? '',
                },
              })
            }
          >
            <ListItemIcon>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            Organization Admin
          </MenuItem>
        )}

        {canAccessSystemAdmin && (
          <MenuItem onClick={() => settingNavigateTo('SYSTEM_ADMIN')}>
            <ListItemIcon>
              <ManageAccountsIcon fontSize="small" />
            </ListItemIcon>
            System Admin
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      <IconButton
        edge="end"
        aria-label="account menu"
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        sx={{ color: iconColor, p: 0.5 }}
      >
        {initials ? (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: tokens.avatarBg,
              color: tokens.avatarText,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        ) : (
          <AccountCircle style={{ fontSize: 28, color: iconColor }} />
        )}
      </IconButton>
    </>
  );
};

export default TopBarAccountMenu;
